import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatPlanPrice, planDisplayName, type BillingCycle, type PlanInfo } from '@/lib/plans';

interface BandChangeConfirmDialogProps {
  open: boolean;
  /** Aktuelles Band des Abos; null, wenn unbekannt. */
  from: PlanInfo | null;
  to: PlanInfo | null;
  cycle: BillingCycle;
  /**
   * Intervall des LAUFENDEN Abos. Der alte Preis steht in diesem Intervall,
   * nicht im Ziel-Intervall des Zyklus-Schalters: ein Monatskunde, der auf
   * Jaehrlich schaltet, zahlt heute weiter pro Monat. Fehlt es, gilt `cycle`.
   */
  fromCycle?: BillingCycle;
  currency: 'eur' | 'usd';
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Der EINE Bandwechsel-Dialog fuer Preisseite und Einstellungen (UX-Audit K36).
 * Nennt Richtung, alten und neuen Preis und nur den zutreffenden Zeitpunkt:
 * groesseres Band sofort anteilig, kleineres zum Periodenende (so verfaehrt
 * manage-subscription: Upgrade mit proration_behavior always_invoice,
 * Downgrade als Subscription Schedule zum Periodenende). Vorher standen beide
 * Saetze immer zusammen und ohne Betrag im Dialog.
 */
export const BandChangeConfirmDialog: React.FC<BandChangeConfirmDialogProps> = ({
  open,
  from,
  to,
  cycle,
  fromCycle,
  currency,
  onConfirm,
  onCancel,
}) => {
  const { t, i18n } = useTranslation('pricing');
  const language = i18n?.language;
  const priceOf = (p: PlanInfo, c: BillingCycle = cycle) =>
    `${formatPlanPrice(p, currency, c, language)}${c === 'yearly' ? t('perYear') : t('perMonth')}`;

  // Richtung wie classifyBandChange in manage-subscription: die Kapazitaet
  // entscheidet. Bei gleicher Kapazitaet haengt der Zeitpunkt am Intervall
  // (month->year sofort, sonst kein Wechsel), das kennt dieser Dialog nicht;
  // er nennt dann keinen Zeitpunkt statt eines falschen.
  const direction: 'upgrade' | 'downgrade' | 'neutral' = !to
    ? 'neutral'
    : !from
      ? 'upgrade'
      : to.productLimit > from.productLimit
        ? 'upgrade'
        : to.productLimit < from.productLimit
          ? 'downgrade'
          : 'neutral';
  const toName = to ? planDisplayName(to, language, t) : '';

  let body = '';
  if (to && from && direction !== 'neutral') {
    body = t(direction === 'upgrade' ? 'bandChange.upgradeBody' : 'bandChange.downgradeBody', {
      from: planDisplayName(from, language, t),
      fromPrice: priceOf(from, fromCycle ?? cycle),
      to: toName,
      toPrice: priceOf(to),
    });
  } else if (to) {
    body = t('bandChange.bodyNoFrom', { to: toName, toPrice: priceOf(to) });
  }

  const title =
    direction === 'upgrade'
      ? t('bandChange.upgradeTitle', { to: toName })
      : direction === 'downgrade'
        ? t('bandChange.downgradeTitle', { to: toName })
        : t('bandChange.confirmTitle');

  return (
    <AlertDialog open={open && !!to} onOpenChange={(v) => !v && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>{t('bandChange.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{t('bandChange.confirm')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
